<template>
  <div
    class="card-teal flex flex-col gap-30 p-20 text-white max-md:text-center md:gap-45 md:px-48 md:py-48 lg:px-72"
  >
    <div class="flex gap-10 max-md:flex-col max-md:items-center md:gap-60">
      <h2 class="xl:text-39 md:text-29 text-24" v-text="'Find My School'" />
      <div class="flex items-center gap-10">
        <div
          class="flex h-40 w-40 items-center justify-center rounded-full border-2"
          :aria-label="`Step 1 of 3: Select state${stage >= 1 ? ', completed' : ''}`"
          :aria-current="stage === 0 ? 'step' : undefined"
          v-text="stage < 1 ? '1' : '✓'"
        />
        <div class="h-0 w-44 border" :class="{ 'text-teal-light': stage < 1 }" />
        <div
          class="flex h-40 w-40 items-center justify-center rounded-full border-2"
          :class="{ 'text-teal-light': stage < 1 }"
          :aria-label="`Step 2 of 3: Select school${stage >= 2 ? ', completed' : ''}`"
          :aria-current="stage === 1 ? 'step' : undefined"
          v-text="stage < 2 ? '2' : '✓'"
        />
        <div class="h-0 w-44 border" :class="{ 'text-teal-light': stage < 2 }" />
        <div
          class="flex h-40 w-40 items-center justify-center rounded-full border-2"
          :class="{ 'text-teal-light': stage < 2 }"
          aria-label="Step 3 of 3: View documents"
          :aria-current="stage === 2 ? 'step' : undefined"
          v-text="'3'"
        />
      </div>
    </div>
    <div v-if="stage === 0" class="flex flex-col gap-15">
      <p
        class="xl:text-17 text-15"
        v-text="'1. Please select the state where your school is located'"
      />
      <DropDownList
        v-model="state"
        class="teal"
        text-field="STA_Name"
        aria-label="Select your state"
        :data-items="props.stateList"
      />
    </div>
    <div v-else-if="stage === 1" class="flex flex-col gap-15">
      <p v-text="'2. Please select your school'" />
      <DropDownList
        v-model="school"
        class="teal"
        text-field="SCH_DisplayName"
        aria-label="Select your school"
        :data-items="schoolList"
      />
    </div>
    <div v-else-if="stage === 2" class="font-poppins flex flex-col gap-15">
      <p v-text="`3. ${school?.SCH_DisplayName}`" />
      <div class="h-56" />
    </div>
    <div class="flex justify-between">
      <button v-if="stage > 0" class="btn-outline-orange w-170" @click="stage--" v-text="'Back'" />
      <div v-else />
      <button
        v-if="stage < 2"
        class="btn-outline-orange w-170"
        :disabled="(stage === 0 && !state) || (stage === 1 && !school)"
        @click="stage++"
        v-text="'Next'"
      />
    </div>
  </div>
  <div
    v-if="stage === 2 && files && filteredYears.length"
    class="flex flex-col gap-30 text-white *:not-first:px-20 *:last:pb-20 md:*:not-first:px-40 md:*:last:pb-48 lg:*:not-first:px-72"
    :class="[tab === FileYear.current ? 'card-teal' : 'card-purple']"
  >
    <div class="flex" role="tablist">
      <button
        v-for="key in filteredYears"
        :key="key"
        class="flex h-40 flex-1 items-center justify-center border-b border-gray-700 not-last:border-r first:rounded-tl-2xl last:rounded-tr-2xl"
        :class="[
          key === FileYear.current ? 'bg-teal' : 'bg-purple',
          { 'border-b-0! pb-1': tab === key, 'cursor-default': filteredYears.length < 2 },
        ]"
        role="tab"
        :aria-selected="tab === key"
        @click="tab = key"
        v-text="`${toProperCase(key)} Year`"
      />
    </div>
    <h3 class="text-25" v-text="'Documents'" />
    <div
      class="font-poppins -mt-10 flex flex-col gap-10"
      :class="[tab === FileYear.current ? '*:border-teal-light' : '*:border-purple-light']"
    >
      <a
        v-for="(href, i) in files[tab].planLinks"
        :key="href"
        class="policy-link"
        :href="href"
        v-text="`Plan Information ${files[tab].planLinks.length > 1 ? i + 1 : ''}`.trim()"
      />
      <a
        v-if="isActivePlan(files[tab].volPlanEndDate)"
        class="policy-link"
        :href="`/${props.type}/enrollment/${school?.SCH_School_PK}`"
        v-text="`Purchase Online`"
      />
      <a
        v-for="(href, i) in files[tab].brochure"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Brochure/Enrollment Form', files[tab].brochure, i)"
      />
      <a
        v-for="(href, i) in files[tab].spanish"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Brochure/Enrollment Form (Spanish)', files[tab].spanish, i)"
      />
      <a
        v-for="(href, i) in files[tab].certificates"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Certificate of Coverage', files[tab].certificates, i)"
      />
      <a
        v-for="(href, i) in files[tab].athletic"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Athletic Coverage Brochure', files[tab].athletic, i)"
      />
      <a
        v-for="(href, i) in files[tab].needlestick"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Needlestick Coverage Brochure', files[tab].needlestick, i)"
      />
      <a
        v-for="(href, i) in files[tab].uil"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('UIL Activities Coverage Brochure', files[tab].uil, i)"
      />
      <a
        v-for="(href, i) in files[tab].summary"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('All School Activities Coverage Brochure', files[tab].summary, i)"
      />
      <a
        v-for="(href, i) in files[tab].pid"
        :key="href"
        class="policy-pdf"
        :href="href"
        target="_blank"
        rel="noopener noreferrer"
        v-text="docLabel('Policy Identification Form', files[tab].pid, i)"
      />
      <a
        v-if="files[tab].schoolLink"
        class="policy-link"
        :href="files[tab].schoolLink"
        v-text="`School Website`"
      />
      <template v-if="isActivePlan(files[tab].policyEndDate)">
        <a
          v-if="props.type === SchoolType.College || school?.STA_Code !== 'TX'"
          class="policy-pdf"
          :href="`${env.PUBLIC_DOCUMENTS_URL}/${props.type}/claim/claim.pdf`"
          target="_blank"
          rel="noopener noreferrer"
          v-text="'Claim Form'"
        />
        <template v-else>
          <a
            class="policy-pdf"
            :href="`${env.PUBLIC_DOCUMENTS_URL}/${props.type}/claim/claim-tx.pdf`"
            target="_blank"
            rel="noopener noreferrer"
            v-text="'Claim Form'"
          />
          <a
            class="policy-pdf"
            :href="`${env.PUBLIC_DOCUMENTS_URL}/${props.type}/claim/claim-tx-sp.pdf`"
            target="_blank"
            rel="noopener noreferrer"
            v-text="'Claim Form (Spanish)'"
          />
        </template>
        <button class="policy-link w-full" @click="showClaimVideo = true">
          How to Submit a Claim
        </button>
        <a
          v-for="(href, i) in files[tab].notice"
          :key="href"
          class="policy-pdf"
          :href="href"
          target="_blank"
          rel="noopener noreferrer"
          v-text="docLabel('Other Important Information', files[tab].notice, i, false)"
        />
      </template>
    </div>
  </div>
  <DialogModal :open="showClaimVideo" panel-class="max-w-1200">
    <div class="flex flex-col">
      <div class="flex items-center justify-between px-24 py-16">
        <span class="font-poppins text-17 font-semibold" v-text="'How to Submit a Claim'" />
        <button
          class="text-24 leading-none text-gray-500 hover:text-gray-800"
          aria-label="Close video"
          @click="showClaimVideo = false"
          v-text="'×'"
        />
      </div>
      <div class="px-24 pb-24">
        <iframe
          class="aspect-video w-full rounded-xl"
          :src="`https://www.youtube-nocookie.com/embed/${CLAIM_VIDEO_ID}`"
          title="How to Submit a Claim"
          frameborder="0"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share;
          "
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        />
      </div>
    </div>
  </DialogModal>
</template>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import DialogModal from '@/components/inputs/DialogModal.vue';
import DropDownList from '@/components/inputs/DropDownList.vue';
import { env } from '@/scripts/env';
import { useReferenceStore } from '@/stores/reference';
import { SchoolType } from '@sas/shared/constants';
import type { dbo, pas } from '@sas/shared/database';
import { FileYear, type PolicyFilesYear } from '@sas/shared/documents';
import { toProperCase } from '@sas/shared/functions';

const CLAIM_VIDEO_ID = 'BZEZLUiYhV0';

/// Setup
const props = defineProps({
  type: { type: String as PropType<SchoolType>, required: true },
  stateList: { type: Array<dbo.State>, required: true },
});
const store = useReferenceStore();

/// Data
const showClaimVideo = ref(false);
const stage = ref<number>(0);
const state = ref<dbo.State | undefined>(props.stateList[0]);
const school = ref<pas.School | null>(null);
const tab = ref<FileYear>(FileYear.current);

/// Computed
const schoolList = computed<pas.School[]>(() =>
  store.schoolsFor(props.type, state.value?.STA_Code),
);
const files = computed(() => store.filesFor(school.value?.SCH_School_PK));
const filteredYears = computed(() =>
  !files.value ? [] : Object.values(FileYear).filter((x) => hasFiles(files.value![x])),
);

/// Methods
function docLabel(label: string, list: string[], i: number, showYear = true): string {
  const policies: PolicyFilesYear = files.value![tab.value];
  const year: string = showYear ? `${policies.year} - ${policies.year + 1} ` : '';
  const index: string = list.length > 1 ? ` ${i + 1}` : '';
  return `${year}${label}${index}`;
}
function hasFiles(year: PolicyFilesYear): boolean {
  return (
    [
      year.planLinks,
      year.brochure,
      year.spanish,
      year.athletic,
      year.needlestick,
      year.summary,
      year.uil,
      year.pid,
      year.certificates,
    ].some((a) => (a ?? []).length > 0) ||
    !!year.schoolLink ||
    isActivePlan(year.policyEndDate) ||
    isActivePlan(year.volPlanEndDate)
  );
}
function isActivePlan(planEndDate: string | undefined): boolean {
  if (!planEndDate) return false;
  return planEndDate >= new Date().toISOString().slice(0, 10);
}

/// Watchers
// Default the state to the first option whenever the upstream list changes
// (initial hydration from localStorage, then async refresh).
watch(
  () => props.stateList,
  (list) => {
    if (!state.value || !list.some((s) => s.STA_Code === state.value?.STA_Code)) {
      state.value = list[0];
    }
  },
  { immediate: true },
);
// Default the school to the first option whenever the filtered list changes
// (state change or async reference-data arrival).
watch(
  schoolList,
  (list) => {
    if (!school.value || !list.some((s) => s.SCH_School_PK === school.value?.SCH_School_PK)) {
      school.value = list[0] ?? null;
    }
  },
  { immediate: true },
);
// Reset to the first available year tab whenever the file bundle changes (school selection
// changes, async refresh of the reference bundle).
watch(filteredYears, (years) => ([tab.value] = years), { immediate: true });
</script>
